/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { defineRecipe } from 'chakra-v3';

/**
 * Button Recipe - Minimalist approach using theme tokens
 *
 * Usage: <Button variant="cta">Primary Action</Button>
 *        <Button variant="secondary">Secondary Action</Button>
 */
export const buttonRecipe = defineRecipe({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'body', // DM Sans
    fontWeight: 600, // SemiBold
    fontSize: '16px', // Match Figma: 16px
    lineHeight: '1.4', // Match Figma line height
    letterSpacing: '0.08px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    borderRadius: '4px',
    borderWidth: '1px',
    borderStyle: 'solid',
    whiteSpace: 'nowrap',
  },
  variants: {
    variant: {
      cta: {
        bg: 'cta.solid',
        color: 'text.primary',
        // Border is 10% lighter than button color (#055369)
        borderColor: 'color-mix(in srgb, #055369 90%, white 10%)',
        _hover: {
          bg: 'cta.hover',
          // Border is 10% lighter than hover color (#044354)
          borderColor: 'color-mix(in srgb, #044354 90%, white 10%)',
        },
      },
      primary: {
        bg: 'brand.tealDark',
        color: 'text.primary',
        // Border is 10% lighter than button color (#002029)
        borderColor: 'color-mix(in srgb, #002029 90%, white 10%)',
        _hover: {
          bg: 'brand.teal',
          color: 'text.primary',
          // Border is 10% lighter than hover color (#0A313C)
          borderColor: 'border.teal',
        },
      },
      secondary: {
        // Radial gradient background from Figma
        background:
          'radial-gradient(99.74% 65.32% at 48.71% 100%, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)',
        color: 'text.primary',
        // Border uses lighter version of white (10% lighter = more opacity)
        borderColor: 'rgba(255, 255, 255, 0.20)',
        _hover: {
          background:
            'radial-gradient(99.74% 65.32% at 48.71% 100%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
          borderColor: 'rgba(255, 255, 255, 0.25)',
        },
      },
      outline: {
        bg: 'transparent',
        color: 'text.primary',
        borderColor: 'border.teal',
        _hover: {
          bg: 'brand.teal',
          color: 'text.primary',
          // Border remains 10% lighter than teal
          borderColor: 'border.teal',
        },
      },
      ghost: {
        bg: 'transparent',
        color: 'brand.white',
        borderWidth: '0',
        _hover: {
          bg: 'brand.white20',
          color: 'text.primary',
        },
      },
      selected: {
        // Radial gradient background from Figma for selected preference buttons
        background: 'radial-gradient(293.86% 142.07% at 1.02% 0%, rgba(47, 82, 92, 0.50) 0%, #055369 100%)',
        color: 'brand.secondaryBlue2',
        borderColor: 'brand.secondaryBlue2',
        _hover: {
          opacity: 0.8,
        },
      },
      selectedWithIcon: {
        // Solid dark teal background with light blue border and text for selected buttons with icons
        bg: 'cta.solid',
        color: 'brand.secondaryBlue2',
        borderColor: 'brand.secondaryBlue2',
        gap: '8px', // Gap between icon and text (from Figma design)
        _hover: {
          opacity: 0.8,
        },
      },
      evalToggleSelected: {
        bg: '#055369',
        color: 'white',
        borderColor: '#F6F6F6',
        borderWidth: '1px',
        minH: '61px',
        gap: '12px',
        _hover: {
          opacity: 0.95,
        },
      },
      evalToggleUnselected: {
        bg: '#E3F9FF',
        color: '#002029',
        borderColor: 'transparent',
        borderWidth: '1px',
        minH: '61px',
        gap: '12px',
        _hover: {
          bg: '#E3F9FF',
        },
      },
    },
    size: {
      md: {
        // fontSize and lineHeight inherited from base (16px, 1.4)
        px: '10px', // Reduced horizontal padding (Figma shows 16px, reduced for more compact look)
        py: '10px', // Balanced vertical padding (Figma shows 12px, slightly reduced)
        minHeight: 'auto', // Remove fixed min-height
      },
    },
  },
  defaultVariants: {
    variant: 'cta',
    size: 'md',
  },
});

